import React from "react";
import {
  Text,
  makeStyles,
  tokens,
  Card,
  DataGrid,
  DataGridBody,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  DataGridCell,
  TableColumnDefinition,
  createTableColumn,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { FailedDocument } from "../../models/Job";

const useStyles = makeStyles({
  container: {
    maxHeight: "400px",
    overflowY: "auto",
  },
  emptyState: {
    textAlign: "center",
    padding: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
  },
  errorCell: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  timestampCell: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
});

interface FailedDocumentsListProps {
  failedDocuments: FailedDocument[];
  title?: string;
}

export const FailedDocumentsList: React.FC<FailedDocumentsListProps> = ({
  failedDocuments,
  title,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const columns: TableColumnDefinition<FailedDocument>[] = [
    createTableColumn<FailedDocument>({
      columnId: "name",
      compare: (a, b) => a.name.localeCompare(b.name),
      renderHeaderCell: () => t("failedDocuments.name"),
      renderCell: (item) => <Text weight="semibold">{item.name}</Text>,
    }),
    createTableColumn<FailedDocument>({
      columnId: "type",
      compare: (a, b) => a.type.localeCompare(b.type),
      renderHeaderCell: () => <div>{t("failedDocuments.type")}</div>,
      renderCell: (item) => <Text>{item.type}</Text>,
    }),
    createTableColumn<FailedDocument>({
      columnId: "id",
      compare: (a, b) => a.id.localeCompare(b.id),
      renderHeaderCell: () => <div>Id</div>,
      renderCell: (item) => <Text>{item.id}</Text>,
    }),
    createTableColumn<FailedDocument>({
      columnId: "error",
      compare: (a, b) => a.error.localeCompare(b.error),
      renderHeaderCell: () => t("failedDocuments.error"),
      renderCell: (item) => (
        <Text className={classes.errorCell}>{item.error}</Text>
      ),
    }),
    createTableColumn<FailedDocument>({
      columnId: "timestamp",
      compare: (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      renderHeaderCell: () => t("failedDocuments.timestamp"),
      renderCell: (item) => (
        <Text className={classes.timestampCell}>
          {formatTimestamp(item.timestamp)}
        </Text>
      ),
    }),
  ];

  if (failedDocuments.length === 0) {
    return (
      <Card>
        <Text weight="semibold">{title || t("failedDocuments.title")}</Text>

        <Text className={classes.emptyState}>
          {t("failedDocuments.noFailedDocuments")}
        </Text>
      </Card>
    );
  }

  return (
    <Card>
      {title || t("failedDocuments.title")} ({failedDocuments.length})
      <div className={classes.container}>
        <DataGrid
          items={failedDocuments}
          columns={columns}
          sortable
          getRowId={(item) => item.id}
        >
          <DataGridHeader style={{ display: "flex" }}>
            {columns.map((column) => (
              <DataGridHeaderCell key={column.columnId}>
                {column.renderHeaderCell()}
              </DataGridHeaderCell>
            ))}
          </DataGridHeader>
          <DataGridBody<FailedDocument>>
            {({ item }) => (
              <DataGridRow<FailedDocument> key={item.id}>
                {({ renderCell }) => (
                  <DataGridCell>{renderCell(item)}</DataGridCell>
                )}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      </div>
    </Card>
  );
};
